import { useEffect, useMemo, useState } from "react";
import { Box, Text, useBreakpointValue } from "@chakra-ui/react";
import { useHookstate } from "@hookstate/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { appStore } from "../../hookstate/appStore";
import { useAccountData } from "../../hooks/useAccountData";
import type { User } from "../../types";
import { useProfileMutation } from "./hooks/useProfileMutation";
import { useAvatarUploadMutation, useSettingsToast } from "./hooks/useSettingsMutations";
import { AboutYourselfSection } from "./components/AboutYourselfSection";
import { DiscoveryContentSection } from "./components/DiscoveryContentSection";
import { LanguageLocaleSection } from "./components/LanguageLocaleSection";
import { PrivacySection } from "./components/PrivacySection";
import { BetaSection } from "./components/BetaSection";
import { NotificationsSection } from "./components/NotificationsSection";
import { ChangeEmailSection } from "./components/ChangeEmailSection";
import { ChangePasswordSection } from "./components/ChangePasswordSection";
import { CreditsSection } from "./components/CreditsSection";
import type { ProfilePayload } from "./api/profileApi";

type SavedProfileState = {
  username: string;
  fullName: string;
  description: string;
};

function getSavedProfileState(user: User): SavedProfileState {
  return {
    username: user.name || "",
    fullName: user.full_name || "",
    description: user.description || "",
  };
}

export function ProfileSettingsScreen() {
  const [t] = useTranslation();
  const accountData = useAccountData();
  const user = accountData?.user;
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const initData = useHookstate(appStore.init).get({ noproxy: true });
  const countries = useMemo(
    () => (initData?.countries ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [initData?.countries],
  );
  const uiLanguages = useMemo(
    () => (initData?.languages ?? []).filter((l) => l.i18n_active && l.i18n_public),
    [initData?.languages],
  );
  const allLanguages = useMemo(
    () => (initData?.languages ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [initData?.languages],
  );

  // Form state
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [description, setDescription] = useState("");
  const [fullNameError, setFullNameError] = useState<string | null>(null);
  const [countryId, setCountryId] = useState<string>("");
  const [uiLang, setUiLang] = useState("");
  const [releasesLang, setReleasesLang] = useState("");
  const [displayRealName, setDisplayRealName] = useState(false);
  const [displayDonations, setDisplayDonations] = useState(false);
  const [enablePms, setEnablePms] = useState(false);
  const [hideAiResults, setHideAiResults] = useState(false);
  const [showMatureContent, setShowMatureContent] = useState(false);
  const [betaAccess, setBetaAccess] = useState(false);
  const [savedProfile, setSavedProfile] = useState<SavedProfileState | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Avatar upload
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const pendingAvatarUrl = useMemo(
    () => (pendingAvatarFile ? URL.createObjectURL(pendingAvatarFile) : null),
    [pendingAvatarFile],
  );
  useEffect(() => {
    return () => {
      if (pendingAvatarUrl) URL.revokeObjectURL(pendingAvatarUrl);
    };
  }, [pendingAvatarUrl]);
  const { mutateAsync: uploadAvatar, isPending: isUploadingAvatar } = useAvatarUploadMutation();
  const settingsToast = useSettingsToast();

  // Redirect to home if not logged in
  useEffect(() => {
    if (accountData && !accountData.user) navigate("/");
  }, [accountData, navigate]);

  // Initialize form from account data
  useEffect(() => {
    if (user && !initialized) {
      const nextSavedProfile = getSavedProfileState(user);

      setUsername(nextSavedProfile.username);
      setFullName(nextSavedProfile.fullName);
      setDescription(nextSavedProfile.description);
      setCountryId(user.country_id ? String(user.country_id) : "");
      setUiLang(user.ui_lang || "en");
      setReleasesLang(user.releases_lang || "");
      setDisplayRealName(!!user.display_real_name_in_public);
      setDisplayDonations(!!user.display_donations_in_public);
      setEnablePms(!!user.enable_pms);
      setHideAiResults(!!user.hide_ai_results);
      setShowMatureContent(!!user.show_mature_content);
      setBetaAccess(!!user.beta_access);
      setSavedProfile(nextSavedProfile);
      setInitialized(true);
    }
  }, [user, initialized]);

  const { mutate, isPending } = useProfileMutation();

  const handleSaveProfile = async () => {
    if (fullName.trim().length === 0) {
      const validationMessage = t("screens.settings.fullNameRequiredHint");
      setFullNameError(validationMessage);
      settingsToast.error(t("screens.settings.fullNameRequired"), validationMessage);
      return;
    }

    if (pendingAvatarFile) {
      try {
        await uploadAvatar(pendingAvatarFile);
        setPendingAvatarFile(null);
      } catch {
        settingsToast.error("Avatar upload failed", "Please try again.");
        return;
      }
    }

    mutate(
      {
        name: username,
        full_name: fullName,
        description: description,
        country_id: countryId || undefined,
        ui_lang: uiLang,
        releases_lang: releasesLang || null,
        display_real_name_in_public: displayRealName,
        display_donations_in_public: displayDonations,
        enable_pms: enablePms,
        hide_ai_results: hideAiResults,
        show_mature_content: showMatureContent,
        beta_access: betaAccess,
      },
      {
        onSuccess: (response) => {
          const nextUser = response.data.payload.results.user;
          if (!nextUser) {
            return;
          }
          const nextSavedProfile = getSavedProfileState(nextUser);

          setUsername(nextSavedProfile.username);
          setFullName(nextSavedProfile.fullName);
          setDescription(nextSavedProfile.description);
          setSavedProfile(nextSavedProfile);
          setFullNameError(null);
        },
      },
    );
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);

    if (fullNameError && value.trim().length > 0) {
      setFullNameError(null);
    }
  };

  const handleToggle = (field: string, value: boolean) => {
    const setters: Record<string, (v: boolean) => void> = {
      show_mature_content: setShowMatureContent,
      hide_ai_results: setHideAiResults,
      display_real_name_in_public: setDisplayRealName,
      display_donations_in_public: setDisplayDonations,
      enable_pms: setEnablePms,
      beta_access: setBetaAccess,
    };
    setters[field]?.(value);
    mutate({ [field]: value } as ProfilePayload);
  };

  const handleSelect = (field: string, value: string) => {
    const setters: Record<string, (v: string) => void> = {
      country_id: setCountryId,
      ui_lang: setUiLang,
      releases_lang: setReleasesLang,
    };
    setters[field]?.(value);
    mutate({ [field]: value || null } as ProfilePayload);
  };

  const isProfileDirty =
    !!pendingAvatarFile ||
    username !== (savedProfile?.username || "") ||
    fullName !== (savedProfile?.fullName || "") ||
    description !== (savedProfile?.description || "");

  if (!user) {
    return null;
  }

  const sectionDivider = <Box borderTop="1px solid" borderColor="transparent.white.10" my={8} />;

  return (
    <Box maxW="680px" mx="auto" px={isMobile ? 4 : 6} py={isMobile ? 6 : 10}>
      {/* Back link */}
      <Text
        fontSize="md"
        color="transparent.white.50"
        cursor="pointer"
        mb={6}
        onClick={() => navigate(-1)}
        _hover={{ color: "dune.dune" }}
      >
        &larr; {t("screens.settings.back")}
      </Text>

      {/* Page header */}
      <Text
        fontSize={isMobile ? "3xl" : "5xl"}
        fontFamily="heading"
        fontWeight="bold"
        color="dune.dune"
        mb={2}
      >
        {t("screens.settings.pageTitle")}
      </Text>
      <Text fontSize="md" color="transparent.white.60" mb={6}>
        {t("screens.settings.pageSubtitle")}
      </Text>

      {sectionDivider}

      <AboutYourselfSection
        user={user}
        username={username}
        fullName={fullName}
        description={description}
        fullNameError={fullNameError}
        pendingAvatarUrl={pendingAvatarUrl}
        isPending={isPending}
        isUploadingAvatar={isUploadingAvatar}
        isProfileDirty={isProfileDirty}
        onUsernameChange={setUsername}
        onFullNameChange={handleFullNameChange}
        onDescriptionChange={setDescription}
        onAvatarFileSelected={setPendingAvatarFile}
        onSaveProfile={handleSaveProfile}
        onAvatarError={(title, desc) => settingsToast.error(title, desc)}
      />

      {sectionDivider}

      <DiscoveryContentSection
        showMatureContent={showMatureContent}
        hideAiResults={hideAiResults}
        onToggle={handleToggle}
      />

      {sectionDivider}

      <LanguageLocaleSection
        countryId={countryId}
        uiLang={uiLang}
        releasesLang={releasesLang}
        countries={countries}
        uiLanguages={uiLanguages}
        allLanguages={allLanguages}
        isMobile={isMobile}
        onSelect={handleSelect}
      />

      {sectionDivider}

      <PrivacySection
        displayRealName={displayRealName}
        displayDonations={displayDonations}
        enablePms={enablePms}
        onToggle={handleToggle}
      />

      {sectionDivider}

      <NotificationsSection />

      {sectionDivider}

      <BetaSection betaAccess={betaAccess} onToggle={handleToggle} />

      {sectionDivider}

      <ChangeEmailSection currentEmail={user.email_address} />

      {sectionDivider}

      <ChangePasswordSection />

      {sectionDivider}

      <CreditsSection />
    </Box>
  );
}
